import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  HStack,
  VStack,
  IconButton,
  Tooltip,
  Text,
  Badge,
  useColorModeValue,
} from '@chakra-ui/react';
import SeatMapCanvas from '../../../../shared/SeatMapCanvas';
import {
  MdZoomIn,
  MdZoomOut,
  MdFitScreen,
} from 'react-icons/md';

/**
 * VisualSeatPicker:
 * Renders the seating plan on an interactive SVG canvas exactly matching
 * the physical stage, zones, rows, curved rows, and seats layout.
 */
export default function VisualSeatPicker({
  planJson,
  ticketTiers = [],
  seatStatuses = {},
  selectedGuids = [],
  onToggleSeat,
  isDraft = false,
  adminReservationMode = false,
  disabled = false,
}) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);

  const bgColor = useColorModeValue('#F8FAFC', '#0B1437');
  const borderColor = useColorModeValue('#E2E8F0', '#1F2937');
  const textColor = useColorModeValue('gray.700', 'white');
  const controlsBg = useColorModeValue('whiteAlpha.900', 'gray.800');
  const hoveredCardBg = useColorModeValue('white', 'gray.800');

  const width = planJson?.size?.width || 900;
  const height = planJson?.size?.height || 700;

  // Pan and zoom state follows the public picker behavior.
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [hoveredSeat, setHoveredSeat] = useState(null);
  const [showScrollHint, setShowScrollHint] = useState(false);
  const zoomRef = useRef(1);
  const panRef = useRef({ x: 0, y: 0 });
  const userHasInteractedRef = useRef(false);
  const animationFrameRef = useRef(null);
  const scrollHintTimerRef = useRef(null);
  const touchStateRef = useRef({ mode: 'none', startDistance: 0, startZoom: 1, panStartX: 0, panStartY: 0 });
  const selectedSet = useMemo(() => new Set(selectedGuids), [selectedGuids]);

  const setView = (nextZoom, nextPan) => {
    zoomRef.current = nextZoom;
    panRef.current = nextPan;
    setZoom(nextZoom);
    setPan(nextPan);
  };

  const animateTo = (targetZoom, targetPan, duration = 300) => {
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    const startZoom = zoomRef.current;
    const startPan = { ...panRef.current };
    const startTime = performance.now();
    const easeOutCubic = value => 1 - Math.pow(1 - value, 3);

    const step = now => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = easeOutCubic(progress);
      setView(
        startZoom + (targetZoom - startZoom) * eased,
        {
          x: startPan.x + (targetPan.x - startPan.x) * eased,
          y: startPan.y + (targetPan.y - startPan.y) * eased,
        }
      );
      if (progress < 1) animationFrameRef.current = requestAnimationFrame(step);
      else animationFrameRef.current = null;
    };
    animationFrameRef.current = requestAnimationFrame(step);
  };

  const fitToContainer = useCallback((animate = false) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;

    const padding = rect.width < 640 ? 14 : 32;
    const fitZoom = Math.min((rect.width - padding * 2) / width, (rect.height - padding * 2) / height);
    if (!Number.isFinite(fitZoom) || fitZoom <= 0) return;
    const fitPan = {
      x: (rect.width - width * fitZoom) / 2,
      y: (rect.height - height * fitZoom) / 2,
    };
    if (animate) animateTo(fitZoom, fitPan);
    else setView(fitZoom, fitPan);
  }, [width, height]);

  useEffect(() => {
    fitToContainer();
    if (!containerRef.current || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      if (!userHasInteractedRef.current) fitToContainer();
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [fitToContainer]);

  useEffect(() => () => {
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    if (scrollHintTimerRef.current) clearTimeout(scrollHintTimerRef.current);
  }, []);

  const zoomAround = (factor, x, y, min = 0.15, max = 4.5) => {
    const currentZoom = zoomRef.current;
    const nextZoom = Math.min(Math.max(currentZoom * factor, min), max);
    const currentPan = panRef.current;
    setView(nextZoom, {
      x: x - (x - currentPan.x) * (nextZoom / currentZoom),
      y: y - (y - currentPan.y) * (nextZoom / currentZoom),
    });
  };

  const handleWheel = e => {
    setHoveredSeat(null);
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      userHasInteractedRef.current = true;
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      zoomAround(e.deltaY < 0 ? 1.08 : 0.92, e.clientX - rect.left, e.clientY - rect.top);
      return;
    }
    setShowScrollHint(true);
    if (scrollHintTimerRef.current) clearTimeout(scrollHintTimerRef.current);
    scrollHintTimerRef.current = setTimeout(() => setShowScrollHint(false), 2000);
  };

  const zoomAtCenter = (factor, maxZoom) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const currentZoom = zoomRef.current;
    const nextZoom = Math.min(Math.max(currentZoom * factor, 0.15), maxZoom);
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    animateTo(nextZoom, {
      x: centerX - (centerX - panRef.current.x) * (nextZoom / currentZoom),
      y: centerY - (centerY - panRef.current.y) * (nextZoom / currentZoom),
    }, 250);
  };

  const zoomIn = () => {
    userHasInteractedRef.current = true;
    zoomAtCenter(1.35, 4.2);
  };

  const zoomOut = () => {
    userHasInteractedRef.current = true;
    zoomAtCenter(1 / 1.35, 4.5);
  };

  const resetFit = () => {
    userHasInteractedRef.current = false;
    fitToContainer(true);
  };

  const handleDoubleClick = e => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const currentZoom = zoomRef.current;
    const pointX = (e.clientX - rect.left - panRef.current.x) / currentZoom;
    const pointY = (e.clientY - rect.top - panRef.current.y) / currentZoom;
    userHasInteractedRef.current = true;
    const targetZoom = Math.min(currentZoom * 1.6, 3.8);
    animateTo(targetZoom, {
      x: rect.width / 2 - pointX * targetZoom,
      y: rect.height / 2 - pointY * targetZoom,
    });
  };

  const handleMouseDown = e => {
    if (e.button !== 0 && e.button !== 1) return;
    setHoveredSeat(null);
    setIsPanning(true);
    setPanStart({ x: e.clientX - panRef.current.x, y: e.clientY - panRef.current.y });
  };

  const handleMouseMove = e => {
    if (!isPanning) return;
    userHasInteractedRef.current = true;
    setView(zoomRef.current, { x: e.clientX - panStart.x, y: e.clientY - panStart.y });
  };

  const handleMouseUp = () => setIsPanning(false);

  const handleTouchStart = e => {
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      touchStateRef.current = {
        mode: 'pan', startDistance: 0, startZoom: zoomRef.current,
        panStartX: touch.clientX - panRef.current.x,
        panStartY: touch.clientY - panRef.current.y,
      };
    } else if (e.touches.length === 2) {
      const [first, second] = e.touches;
      touchStateRef.current = {
        mode: 'pinch',
        startDistance: Math.hypot(first.clientX - second.clientX, first.clientY - second.clientY),
        startZoom: zoomRef.current,
        panStartX: (first.clientX + second.clientX) / 2 - panRef.current.x,
        panStartY: (first.clientY + second.clientY) / 2 - panRef.current.y,
      };
      userHasInteractedRef.current = true;
    }
  };

  const handleTouchMove = e => {
    if (e.touches.length === 1 && touchStateRef.current.mode === 'pan') {
      const touch = e.touches[0];
      userHasInteractedRef.current = true;
      setView(zoomRef.current, {
        x: touch.clientX - touchStateRef.current.panStartX,
        y: touch.clientY - touchStateRef.current.panStartY,
      });
    } else if (e.touches.length === 2 && touchStateRef.current.mode === 'pinch' && containerRef.current) {
      const [first, second] = e.touches;
      const distance = Math.hypot(first.clientX - second.clientX, first.clientY - second.clientY);
      if (!touchStateRef.current.startDistance) return;
      const rect = containerRef.current.getBoundingClientRect();
      const nextZoom = Math.min(Math.max(
        touchStateRef.current.startZoom * distance / touchStateRef.current.startDistance,
        0.15
      ), 4.5);
      const centerX = (first.clientX + second.clientX) / 2 - rect.left;
      const centerY = (first.clientY + second.clientY) / 2 - rect.top;
      const currentZoom = zoomRef.current;
      setView(nextZoom, {
        x: centerX - (centerX - panRef.current.x) * (nextZoom / currentZoom),
        y: centerY - (centerY - panRef.current.y) * (nextZoom / currentZoom),
      });
    }
  };

  const handleTouchEnd = () => { touchStateRef.current.mode = 'none'; };

  useEffect(() => {
    const handleKeyDown = e => {
      if (!e.ctrlKey && !e.metaKey) return;
      if (e.key === '+' || e.key === '=' || e.code === 'NumpadAdd') {
        e.preventDefault(); zoomIn();
      } else if (e.key === '-' || e.key === '_' || e.code === 'NumpadSubtract') {
        e.preventDefault(); zoomOut();
      } else if (e.key === '0' || e.code === 'Numpad0') {
        e.preventDefault(); resetFit();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [width, height]);

  // Helper to resolve seat GUID
  const getSeatGuid = (seat, row) =>
    seat.seat_guid || seat.uuid || `seat-${row.row_number}-${seat.seat_number}`;

  return (
    <Box
      ref={containerRef}
      position="relative"
      w="100%"
      h="520px"
      bg={bgColor}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="xl"
      overflow="hidden"
      userSelect="none"
      cursor={isPanning ? 'grabbing' : 'grab'}
      onWheel={handleWheel}
      onDoubleClick={handleDoubleClick}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
      touchAction="none"
    >
      {/* Floating Canvas Controls */}
      <VStack
        position="absolute"
        top={3}
        left={3}
        zIndex={10}
        bg={controlsBg}
        p={1}
        borderRadius="xl"
        boxShadow="md"
        spacing={1}
      >
        <Tooltip label="Zoom In" hasArrow>
          <IconButton
            size="xs"
            aria-label="Zoom In"
            icon={<MdZoomIn size={18} />}
            onClick={zoomIn}
          />
        </Tooltip>
        <Tooltip label="Zoom Out" hasArrow>
          <IconButton
            size="xs"
            aria-label="Zoom Out"
            icon={<MdZoomOut size={18} />}
            onClick={zoomOut}
          />
        </Tooltip>
        <Tooltip label="Fit to Screen" hasArrow>
          <IconButton
            size="xs"
            aria-label="Fit to Screen"
            icon={<MdFitScreen size={18} />}
            onClick={resetFit}
          />
        </Tooltip>
      </VStack>

      {showScrollHint && (
        <Box position="absolute" top={4} left="50%" transform="translateX(-50%)" zIndex={20} pointerEvents="none" px={4} py={2} borderRadius="xl" bg="blackAlpha.800" color="white" fontSize="xs" fontWeight="semibold" boxShadow="xl">
          Hold Ctrl and scroll to zoom
        </Box>
      )}

      {/* Floating Live Seat Details on Hover */}
      {hoveredSeat && (
        <Box
          position="absolute"
          bottom={3}
          left={3}
          zIndex={10}
          bg={hoveredCardBg}
          px={3}
          py={1.5}
          borderRadius="md"
          boxShadow="lg"
          border="1px solid"
          borderColor={borderColor}
          fontSize="xs"
        >
          <HStack spacing={2}>
            <Badge colorScheme={hoveredSeat.colorScheme}>
              {hoveredSeat.statusLabel}
            </Badge>
            <Text fontWeight="bold" color={textColor}>
              {hoveredSeat.zoneName} · {hoveredSeat.rowLabel} · Seat {hoveredSeat.seatNumber}
            </Text>
            {hoveredSeat.attendeeName && (
              <Text color="gray.500">({hoveredSeat.attendeeName})</Text>
            )}
          </HStack>
        </Box>
      )}

      {/* SVG Seating Canvas */}
      <svg
        ref={svgRef}
        width="100%"
        height="100%"
        style={{ display: 'block', width: '100%', height: '100%' }}
      >
        <SeatMapCanvas
          planJson={planJson}
          seatStatuses={seatStatuses}
          selectedGuids={selectedGuids}
          ticketTiers={ticketTiers}
          zoom={zoom}
          pan={pan}
          disabled={disabled}
          allowReservedSelection
          adminReservationMode={adminReservationMode}
          isDraft={isDraft}
          backgroundFill={bgColor}
          boundaryStroke={borderColor}
          onSeatClick={(event, zone, row, seat) => {
            const isMobileOrTouch = typeof window !== 'undefined' && (
              window.matchMedia('(pointer: coarse)').matches || window.innerWidth < 768 || 'ontouchstart' in window
            );
            if (isMobileOrTouch && zoomRef.current < 1.15 && containerRef.current) {
              userHasInteractedRef.current = true;
              const rect = containerRef.current.getBoundingClientRect();
              const seatX = (zone.position?.x || 0) + (row.position?.x || 0) + (seat.position?.x || 0);
              const seatY = (zone.position?.y || 0) + (row.position?.y || 0) + (seat.position?.y || 0);
              const targetZoom = 1.3;
              animateTo(targetZoom, {
                x: rect.width / 2 - seatX * targetZoom,
                y: rect.height / 2 - seatY * targetZoom,
              }, 300);
              return;
            }
            const guid = getSeatGuid(seat, row);
            const status = isDraft
              ? (selectedSet.has(guid) ? 'draft_reserved' : 'available')
              : (seatStatuses[guid]?.status || 'available');
            onToggleSeat({
              guid,
              number: seat.seat_number,
              status,
              rowName: row.row_label || `Row ${row.row_number || ''}`,
              zoneName: zone.name || zone.label || 'Zone',
            });
          }}
          onSeatHover={(event, zone, row, seat, state) => {
            const statusLabels = {
              available: ['Available', 'blue'],
              unavailable: ['Guest Reserved (Click to release)', 'gray'],
              reserved: ['Guest Reserved (Click to release)', 'gray'],
              booked: ['Sold / Booked', 'gray'],
              sold: ['Sold / Booked', 'gray'],
              held: ['Held by Buyer', 'gray'],
              hold: ['Held by Buyer', 'gray'],
            };
            const [statusLabel, colorScheme] = statusLabels[state.status] || statusLabels.available;
            setHoveredSeat({
              guid: state.guid,
              seatNumber: seat.seat_number,
              rowLabel: row.row_label || `Row ${row.row_number || ''}`,
              zoneName: zone.name || zone.label || 'Zone',
              statusLabel,
              colorScheme,
              attendeeName: seatStatuses[state.guid]?.attendee_name,
            });
          }}
          onSeatLeave={() => setHoveredSeat(null)}
        />
      </svg>
    </Box>
  );
}
